import type { ParseKeys } from "i18next";

import {
  EMeetingFilterStatus,
  EMeetingStatus,
  type IGatedMeetingProvider,
} from "../types/meetings";

export const MEETING_BY_UUID_QUERY_KEY = "meeting-by-uuid";

// NOTE: NSAI-5140 / NSAI-4812 — how often the meeting-details query re-checks a
// meeting that is still being processed. A meeting mid-pipeline is the one case
// where that screen goes stale on its own: the status changes server-side, and
// the details screen gates media playback on it, so a stale status keeps the
// recording locked after the run has finished.
//
// Both clients need the timer, for different reasons. Mobile has no
// notifications socket at all. Web does hear MEETING_STATUS_CHANGED on it and
// invalidates from there, so for web this is a *fallback* for a socket that is
// down or blocked — reachable since NSAI-4812 made an in-progress meeting
// openable in the first place. Only runs while the meeting is in progress;
// every other status is terminal and cannot change without a user action that
// invalidates the query itself.
export const MEETING_IN_PROGRESS_REFETCH_INTERVAL_MS = 15000;
export const MEETINGS_QUERY_KEY = "meetings";
export const MEETING_SHARES_QUERY_KEY = "meeting-shares";
export const MEETING_PUBLIC_SHARE_QUERY_KEY = "meeting-public-share";
// NOTE: The owner's whole set of live public links (NSAI-5027) — distinct from
// MEETING_PUBLIC_SHARE_QUERY_KEY, which is scoped to a single meeting. Mutating
// a share invalidates both.
export const MEETING_PUBLIC_SHARES_QUERY_KEY = "meeting-public-shares";
export const PUBLIC_SHARED_MEETING_QUERY_KEY = "public-shared-meeting";

// NOTE: Header carrying the short-lived share-access token an anonymous viewer
// receives after entering the correct password (NSAI-3853). Must match the
// server constant MEETING_SHARE_ACCESS_TOKEN_HEADER.
export const MEETING_SHARE_ACCESS_TOKEN_HEADER = "x-meeting-share-token";

// NOTE: Machine-readable error codes the anonymous share viewer branches on to
// tell an invalid link from a revoked/expired one and from a wrong password.
// Keep in sync with server MEETING_PUBLIC_SHARE_ERROR_CODES.
export const MEETING_PUBLIC_SHARE_ERROR_CODES = {
  NOT_FOUND: "MEETING_SHARE_NOT_FOUND",
  REVOKED: "MEETING_SHARE_REVOKED",
  EXPIRED: "MEETING_SHARE_EXPIRED",
  INVALID_PASSWORD: "MEETING_SHARE_INVALID_PASSWORD",
  // NOTE: The link is live but its password was regenerated after the viewer's
  // access token was minted (NSAI-5029) — they need to re-enter the new
  // password, they did not get it wrong.
  PASSWORD_ROTATED: "MEETING_SHARE_PASSWORD_ROTATED",
} as const;

// NOTE: Socket.IO namespace an open public-share viewer subscribes to so the
// owner revoking the link takes effect without a refresh (NSAI-5028). Must
// match MEETING_PUBLIC_SHARE_WS_NAMESPACE in
// apps/nsai/server/src/meetings/meeting-public-share.constants.ts.
export const MEETING_PUBLIC_SHARE_WS_NAMESPACE = "/api/meeting-share";

// NOTE: Events pushed on that namespace. Keep in sync with the server enum of
// the same name.
export enum EMeetingPublicShareWsEvent {
  SHARE_REVOKED = "meeting-share:revoked",
}

// NOTE: Fallback re-validation cadence for an open viewer session. The WS push
// is what makes revocation feel instant; this poll is the safety net for a
// viewer whose socket never connected (networks that block websockets) or that
// dropped while a revoke was broadcast — a push delivered to a dead socket is
// lost, there is no replay.
export const PUBLIC_SHARE_VALIDITY_POLL_INTERVAL_MS = 30_000;

// NOTE: Maximum users returned by the Share Meeting user-search dropdown.
export const SHARE_USER_SEARCH_LIMIT = 500 as const;

// NOTE: Below this duration (in seconds) we treat a PROCESSED meeting with
// no notes/transcript as "too short to transcribe" and surface an explanatory
// notice in the UI. Recorder pipelines need ~10–30s of audio to produce
// useful output; 30s is a forgiving floor that catches trivial test joins
// without flagging real short calls.
export const SHORT_MEETING_THRESHOLD_SECONDS = 30 as const;

// NOTE: Hard cap on user-driven processor retries per meeting. Must
// stay in lockstep with MAX_MEETING_RETRY_COUNT in
// server/src/meetings/meetings.constants.ts. UI uses this with
// IMeeting.retryCount to decide between showing the Retry button and
// the "max retries reached" badge. Reset to 0 server-side when the
// workflow callback flips status to PROCESSED — a successful run
// restores the full retry budget.
export const MAX_MEETING_RETRY_COUNT = 3 as const;

export const CALENDARS_QUERY_KEY = "calendars";
export const CALENDARS_REFETCH_INTERVAL_MS = 30_000;
export const CALENDAR_EVENTS_QUERY_KEY = "calendar-events";

// NOTE: UI cooldown after a manual calendar refresh. Mirrors the per-user
// CALENDAR_REFRESH_THROTTLE window (5s) in
// apps/nsai/server/src/calendar/calendar.constants.ts — keep both in sync.
export const CALENDAR_REFRESH_COOLDOWN_MS = 5_000;
export const UPLOADED_MEETINGS_QUERY_KEY = "uploaded-meetings";

// NOTE: 25 distinct colors for calendar connection indicators. Must match server palette.
export const CALENDAR_CONNECTION_COLORS = [
  "#4285F4",
  "#34A853",
  "#EA4335",
  "#FBBC04",
  "#FF6D01",
  "#46BDC6",
  "#7B1FA2",
  "#E91E63",
  "#795548",
  "#0078D4",
  "#00ACC1",
  "#8BC34A",
  "#FF5722",
  "#9C27B0",
  "#3F51B5",
  "#009688",
  "#CDDC39",
  "#FF9800",
  "#607D8B",
  "#F44336",
  "#2196F3",
  "#4CAF50",
  "#FFC107",
  "#673AB7",
  "#00BCD4",
] as const;

// NOTE: Maps EMeetingFilterStatus values to translation keys.
// All keys live in i18n/locales/{en,ru,ar}/call-assistant.json under
// `header.filter_status.*` — keep both in sync when adding values.
export const FILTER_STATUS_TRANSLATION_KEYS: Record<
  EMeetingFilterStatus,
  ParseKeys<"callAssistant">
> = {
  [EMeetingFilterStatus.ALL]: "header.filter_status.all",
  [EMeetingFilterStatus.QUEUED]: "header.filter_status.queued",
  [EMeetingFilterStatus.PREPARING]: "header.filter_status.preparing",
  [EMeetingFilterStatus.JOINING_MEETING]:
    "header.filter_status.joining_meeting",
  [EMeetingFilterStatus.RECORDING]: "header.filter_status.recording",
  [EMeetingFilterStatus.RECORDING_FAILED]:
    "header.filter_status.recording_failed",
  [EMeetingFilterStatus.TRANSCRIBING]: "header.filter_status.transcribing",
  [EMeetingFilterStatus.TRANSCRIPTION_FAILED]:
    "header.filter_status.transcription_failed",
  [EMeetingFilterStatus.NO_AUDIO_TRACK]: "header.filter_status.no_audio_track",
  [EMeetingFilterStatus.NO_SPEECH_DETECTED]:
    "header.filter_status.no_speech_detected",
  [EMeetingFilterStatus.SUMMARIZING]: "header.filter_status.summarizing",
  [EMeetingFilterStatus.SUMMARIZATION_FAILED]:
    "header.filter_status.summarization_failed",
  [EMeetingFilterStatus.PROCESSED]: "header.filter_status.processed",
  [EMeetingFilterStatus.CANCELLED]: "header.filter_status.cancelled",
  [EMeetingFilterStatus.BOT_NOT_ADMITTED]:
    "header.filter_status.bot_not_admitted",
  [EMeetingFilterStatus.BOT_LOBBY_TIMEOUT]:
    "header.filter_status.bot_lobby_timeout",
  [EMeetingFilterStatus.FAILED]: "header.filter_status.failed",
  [EMeetingFilterStatus.ARCHIVED]: "header.filter_status.archived",
};

// NOTE: Maps EMeetingFilterStatus values to empty state translation keys.
// Keys live in i18n/locales/{en,ru,ar}/call-assistant.json under
// `list.empty_status.*` — keep both in sync when adding values.
export const EMPTY_STATUS_TRANSLATION_KEYS: Record<
  EMeetingFilterStatus,
  { title: ParseKeys<"callAssistant">; description: ParseKeys<"callAssistant"> }
> = {
  [EMeetingFilterStatus.ALL]: {
    title: "list.empty_status.all.title",
    description: "list.empty_status.all.description",
  },
  [EMeetingFilterStatus.QUEUED]: {
    title: "list.empty_status.queued.title",
    description: "list.empty_status.queued.description",
  },
  [EMeetingFilterStatus.PREPARING]: {
    title: "list.empty_status.preparing.title",
    description: "list.empty_status.preparing.description",
  },
  [EMeetingFilterStatus.JOINING_MEETING]: {
    title: "list.empty_status.joining_meeting.title",
    description: "list.empty_status.joining_meeting.description",
  },
  [EMeetingFilterStatus.RECORDING]: {
    title: "list.empty_status.recording.title",
    description: "list.empty_status.recording.description",
  },
  [EMeetingFilterStatus.RECORDING_FAILED]: {
    title: "list.empty_status.recording_failed.title",
    description: "list.empty_status.recording_failed.description",
  },
  [EMeetingFilterStatus.TRANSCRIBING]: {
    title: "list.empty_status.transcribing.title",
    description: "list.empty_status.transcribing.description",
  },
  [EMeetingFilterStatus.TRANSCRIPTION_FAILED]: {
    title: "list.empty_status.transcription_failed.title",
    description: "list.empty_status.transcription_failed.description",
  },
  [EMeetingFilterStatus.NO_AUDIO_TRACK]: {
    title: "list.empty_status.no_audio_track.title",
    description: "list.empty_status.no_audio_track.description",
  },
  [EMeetingFilterStatus.NO_SPEECH_DETECTED]: {
    title: "list.empty_status.no_speech_detected.title",
    description: "list.empty_status.no_speech_detected.description",
  },
  [EMeetingFilterStatus.SUMMARIZING]: {
    title: "list.empty_status.summarizing.title",
    description: "list.empty_status.summarizing.description",
  },
  [EMeetingFilterStatus.SUMMARIZATION_FAILED]: {
    title: "list.empty_status.summarization_failed.title",
    description: "list.empty_status.summarization_failed.description",
  },
  [EMeetingFilterStatus.PROCESSED]: {
    title: "list.empty_status.processed.title",
    description: "list.empty_status.processed.description",
  },
  [EMeetingFilterStatus.CANCELLED]: {
    title: "list.empty_status.cancelled.title",
    description: "list.empty_status.cancelled.description",
  },
  [EMeetingFilterStatus.BOT_NOT_ADMITTED]: {
    title: "list.empty_status.bot_not_admitted.title",
    description: "list.empty_status.bot_not_admitted.description",
  },
  [EMeetingFilterStatus.BOT_LOBBY_TIMEOUT]: {
    title: "list.empty_status.bot_lobby_timeout.title",
    description: "list.empty_status.bot_lobby_timeout.description",
  },
  [EMeetingFilterStatus.FAILED]: {
    title: "list.empty_status.failed.title",
    description: "list.empty_status.failed.description",
  },
  [EMeetingFilterStatus.ARCHIVED]: {
    title: "list.empty_status.archived.title",
    description: "list.empty_status.archived.description",
  },
};

export const MAX_UNWRAP_ITERATIONS = 5;

// NOTE: Display names of the providers accepted unconditionally (no feature
// flag), interpolated into the "invalid meeting link" validation message.
// Mirrors ALWAYS_ALLOWED_PROVIDER_NAMES in
// apps/nsai/server/src/meetings/meetings.constants.ts — keep both in sync.
// The server-side meetings.constants.spec.ts pins that list, so changing this
// one without updating the server will fail CI (this package has no tests).
export const ALWAYS_ALLOWED_PROVIDER_NAMES: readonly string[] = [
  "Google Meet",
  "Zoom",
  "Jitsi",
  "Microsoft Teams",
];

// NOTE: Mirrors the per-provider regexes in
// apps/nsai/server/src/cron/cron.constants.ts (GOOGLE_MEET_LINK_REGEX,
// TEAMS_MEETING_LINK_REGEX, ZOOM_MEETING_LINK_REGEX) — keep the URL-body
// character classes ([^\s<>"']+) in sync when either side changes.
// Provider path/host coverage (rationale lives on the server patterns):
// - Google Meet: optional /u/<n>/ multi-account prefix and /lookup/ edu form.
// - Teams: teams.live.com is the personal/free host alongside
//   teams.microsoft.com.
// - Zoom: /j/ (join), /s/ (start), /w/ (webinar), and the web client / PWA
//   forms /wc/<id>/... (what Zoom shows in the browser address bar) and
//   /wc/join/<id> (the join route the server normalizes to before dispatch).
export const MEETING_URL_REGEX =
  /^https:\/\/(meet\.google\.com\/(?:u\/\d+\/)?(?:lookup\/)?[a-zA-Z0-9-]+|teams\.(?:microsoft|live)\.com\/(?:l\/meetup-join\/|meet\/)[^\s<>"']+|(?:[a-zA-Z0-9-]+\.)?zoom\.us\/(?:wc\/(?:join\/)?|[jsw]\/)\d+)/;

// NOTE: NSAI-8362 — Telemost serves the same calls from the consumer host
// (telemost.yandex.ru) and the Yandex 360 business host
// (telemost.360.yandex.ru), and besides the classic `/j/<id>` room link
// Yandex 360 hands out `/link#<uuid>` links whose room id lives in the
// fragment. Mirrors YANDEX_TELEMOST_LINK_REGEX in
// apps/nsai/server/src/cron/cron.constants.ts — keep both in sync.
export const YANDEX_TELEMOST_URL_REGEX =
  /^https:\/\/telemost\.(?:360\.)?yandex\.ru\/(?:j\/[a-zA-Z0-9]+|link#[a-zA-Z0-9-]+)/;

export const KONTUR_TALK_URL_REGEX =
  /^https:\/\/[a-zA-Z0-9]+\.ktalk\.ru\/[a-zA-Z0-9_-]+/;

// NOTE: SaluteJazz cloud uses two interchangeable hosts (salutejazz.ru and
// jazz.sber.ru). The path is always `/calls/<id>` and IDs are strictly
// alphanumeric. Mirrors SALUTEJAZZ_LINK_REGEX in
// apps/nsai/server/src/cron/cron.constants.ts — keep both in sync.
export const SALUTEJAZZ_URL_REGEX =
  /^https:\/\/(?:salutejazz\.ru|jazz\.sber\.ru)\/calls\/[a-zA-Z0-9]+/;

export const JITSI_URL_REGEX =
  /^https:\/\/meet\.jit\.si\/[a-zA-Z0-9_-]+(?:[?#][^\s<>]*)?$/;

// NOTE: Characters that never appear unencoded in a real meeting link. The
// provider allowlist regexes are anchored only at the start, so a value like
// "https://meet.google.com/x evil" matches on its valid prefix; rejecting these
// characters stops trailing garbage from passing form validation. Mirrors
// UNSAFE_MEETING_URL_CHARS_REGEX in
// apps/nsai/server/src/meetings/meetings.constants.ts (the server applies the
// same guard in assert-meeting-url-allowed.ts) — keep both in sync.
export const UNSAFE_MEETING_URL_CHARS_REGEX = /[\s<>"'`]/;

// NOTE: Options for the human-readable date+time meeting title (e.g.
// "Wednesday, July 1, 2026 at 6:50 PM"). Locale is pinned to en-US so the
// format is stable across platforms and matches the backend fallback.
export const TITLE_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

// NOTE: Matches legacy fallback titles that were stored as a raw
// new Date().toISOString() value (always UTC, "...Z"), optionally
// "Meeting "-prefixed. Anchored to the whole title so real titles that merely
// contain a timestamp are left untouched.
export const LEGACY_ISO_TITLE_REGEX =
  /^(?:Meeting )?(\d{4}-\d{2}-\d{2}T[\d:.]+Z)$/;

// NOTE: Data-driven list of the flag-gated providers — display name, the
// anchored URL pattern, and the IMeetingProviderFlags key that enables it.
// isValidMeetingUrl and getSupportedMeetingProvidersLabel both loop over this,
// so adding a provider is one entry here (plus its regex and a flag field).
// Mirrors GATED_MEETING_PROVIDERS in
// apps/nsai/server/src/meetings/meetings.constants.ts — keep both in sync.
// The server-side meetings.constants.spec.ts pins the display names + flag
// keys (in the same order), so changing this list without updating the server
// will fail CI (this package has no tests).
export const GATED_MEETING_PROVIDERS: readonly IGatedMeetingProvider[] = [
  {
    displayName: "Yandex Telemost",
    urlRegex: YANDEX_TELEMOST_URL_REGEX,
    flagKey: "isYandexTelemostEnabled",
  },
  {
    displayName: "Kontur.Talk",
    urlRegex: KONTUR_TALK_URL_REGEX,
    flagKey: "isKonturTalkEnabled",
  },
  {
    displayName: "SaluteJazz",
    urlRegex: SALUTEJAZZ_URL_REGEX,
    flagKey: "isSaluteJazzEnabled",
  },
];

// NOTE: Maps EMeetingStatus values to badge color variants and the i18n key
// shared across the browser extension and any other client that adopts a
// richer status badge. Keys live in
// i18n/locales/{en,ru,ar}/call-assistant-extension.json under `meeting_status.*`.
//
// `variant` aligns with shadcn/ui Badge variants: warning (yellow, recording
// activity), primary (blue, processing activity), success (green, done),
// destructive (red, failed), secondary (gray, queued).
//
// `hasPulse` enables a CSS pulse animation; turn off for terminal states.
export interface IMeetingStatusBadgeConfig {
  variant: "warning" | "primary" | "success" | "destructive" | "secondary";
  hasPulse: boolean;
  i18nKey: ParseKeys<"callAssistantExtension">;
}

// NOTE: NSAI-5561 — one sentence per status explaining what is happening at
// that step, because the badge's one- or two-word label ("Processing media")
// does not tell a user what the file is going through. Shown on hover / focus
// on web and on tap on mobile. A `Record` over the enum, so a new status fails
// the build until it gets its own explanation.
export const MEETING_STATUS_DESCRIPTION_I18N_KEY: Record<
  EMeetingStatus,
  ParseKeys<"callAssistantExtension">
> = {
  [EMeetingStatus.QUEUED]: "meeting_status_description.queued",
  [EMeetingStatus.PREPARING]: "meeting_status_description.preparing",
  [EMeetingStatus.JOINING_MEETING]:
    "meeting_status_description.joining_meeting",
  [EMeetingStatus.RECORDING]: "meeting_status_description.recording",
  [EMeetingStatus.RECORDING_UPLOADING]:
    "meeting_status_description.recording_uploading",
  [EMeetingStatus.RECORDING_FAILED]:
    "meeting_status_description.recording_failed",
  [EMeetingStatus.PROCESSING_MEDIA]:
    "meeting_status_description.processing_media",
  [EMeetingStatus.TRANSCRIBING]: "meeting_status_description.transcribing",
  [EMeetingStatus.TRANSCRIPTION_FAILED]:
    "meeting_status_description.transcription_failed",
  [EMeetingStatus.NO_AUDIO_TRACK]: "meeting_status_description.no_audio_track",
  [EMeetingStatus.NO_SPEECH_DETECTED]:
    "meeting_status_description.no_speech_detected",
  [EMeetingStatus.SUMMARIZING]: "meeting_status_description.summarizing",
  [EMeetingStatus.SUMMARIZATION_FAILED]:
    "meeting_status_description.summarization_failed",
  [EMeetingStatus.PROCESSED]: "meeting_status_description.processed",
  [EMeetingStatus.CANCELLED]: "meeting_status_description.cancelled",
  [EMeetingStatus.BOT_NOT_ADMITTED]:
    "meeting_status_description.bot_not_admitted",
  [EMeetingStatus.BOT_LOBBY_TIMEOUT]:
    "meeting_status_description.bot_lobby_timeout",
  [EMeetingStatus.FAILED]: "meeting_status_description.failed",
};

export const MEETING_STATUS_BADGE_CONFIG: Record<
  EMeetingStatus,
  IMeetingStatusBadgeConfig
> = {
  [EMeetingStatus.QUEUED]: {
    variant: "secondary",
    hasPulse: true,
    i18nKey: "meeting_status.queued",
  },
  [EMeetingStatus.PREPARING]: {
    // NOTE: Primary (blue) + pulse — the runner is actively warming up
    // (Bitwarden fetch, image pull), so it's an in-progress phase but
    // not yet the more user-meaningful "joining meeting" stage.
    variant: "primary",
    hasPulse: true,
    i18nKey: "meeting_status.preparing",
  },
  [EMeetingStatus.JOINING_MEETING]: {
    // NOTE: Warning (yellow/orange) + pulse — visually distinct from
    // RECORDING so users can tell "bot is in the lobby" apart from
    // "bot is being recorded right now." Reuses the recorder-phase
    // family so it stays in the same visual cluster as RECORDING /
    // RECORDING_UPLOADING.
    variant: "warning",
    hasPulse: true,
    i18nKey: "meeting_status.joining_meeting",
  },
  [EMeetingStatus.RECORDING]: {
    variant: "warning",
    hasPulse: true,
    i18nKey: "meeting_status.recording",
  },
  [EMeetingStatus.RECORDING_UPLOADING]: {
    variant: "warning",
    hasPulse: true,
    i18nKey: "meeting_status.recording_uploading",
  },
  [EMeetingStatus.RECORDING_FAILED]: {
    variant: "destructive",
    hasPulse: false,
    i18nKey: "meeting_status.recording_failed",
  },
  [EMeetingStatus.PROCESSING_MEDIA]: {
    variant: "warning",
    hasPulse: true,
    i18nKey: "meeting_status.processing_media",
  },
  [EMeetingStatus.TRANSCRIBING]: {
    variant: "primary",
    hasPulse: true,
    i18nKey: "meeting_status.transcribing",
  },
  [EMeetingStatus.TRANSCRIPTION_FAILED]: {
    variant: "destructive",
    hasPulse: false,
    i18nKey: "meeting_status.transcription_failed",
  },
  [EMeetingStatus.NO_AUDIO_TRACK]: {
    // NOTE: Neutral, not red (NSAI-4749). This status sits outside
    // FAILED_MEETING_STATUSES on purpose — nothing broke, the file simply
    // carried no audio — so red made it indistinguishable from the four real
    // failures. Same neutral treatment as the other intentional outcomes; the
    // "upload a file with sound" instruction is carried by the banner copy.
    variant: "secondary",
    hasPulse: false,
    i18nKey: "meeting_status.no_audio_track",
  },
  [EMeetingStatus.NO_SPEECH_DETECTED]: {
    // NOTE: Same neutral treatment as NO_AUDIO_TRACK — the audio was analysed
    // fine and simply held no speech.
    variant: "secondary",
    hasPulse: false,
    i18nKey: "meeting_status.no_speech_detected",
  },
  [EMeetingStatus.SUMMARIZING]: {
    variant: "primary",
    hasPulse: true,
    i18nKey: "meeting_status.summarizing",
  },
  [EMeetingStatus.SUMMARIZATION_FAILED]: {
    variant: "destructive",
    hasPulse: false,
    i18nKey: "meeting_status.summarization_failed",
  },
  [EMeetingStatus.PROCESSED]: {
    variant: "success",
    hasPulse: false,
    i18nKey: "meeting_status.ready",
  },
  [EMeetingStatus.CANCELLED]: {
    // NOTE: Neutral grey badge — cancellation is intentional, not an error,
    // so destructive (red) would over-signal severity.
    variant: "secondary",
    hasPulse: false,
    i18nKey: "meeting_status.cancelled",
  },
  [EMeetingStatus.BOT_NOT_ADMITTED]: {
    // NOTE: Same neutral grey as CANCELLED. The host refused the bot
    // from the lobby — an intentional decision, not a system error.
    variant: "secondary",
    hasPulse: false,
    i18nKey: "meeting_status.bot_not_admitted",
  },
  [EMeetingStatus.BOT_LOBBY_TIMEOUT]: {
    // NOTE: Same neutral grey as BOT_NOT_ADMITTED. Nobody responded in
    // the lobby — not an error, just no host response.
    variant: "secondary",
    hasPulse: false,
    i18nKey: "meeting_status.bot_lobby_timeout",
  },
  [EMeetingStatus.FAILED]: {
    variant: "destructive",
    hasPulse: false,
    i18nKey: "meeting_status.failed",
  },
};
